import * as React from 'react';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';

export type DeleteConfirmationInput = {
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  testId?: string;
};

export type DeleteConfirmationProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: React.ReactNode;
  confirmingLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  confirmDisabled?: boolean;
  isConfirming?: boolean;
  confirmationInput?: DeleteConfirmationInput;
  testId?: string;
  actionsTestId?: string;
  confirmTestId?: string;
  cancelTestId?: string;
};

/**
 * Shared destructive confirmation surface for tasks, projects and other
 * workspace data. Keep the action order and mobile sizing here so each
 * delete flow uses the same layout.
 */
export function DeleteConfirmation({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = 'Delete',
  confirmingLabel = 'Deleting…',
  cancelLabel = 'Cancel',
  confirmDisabled = false,
  isConfirming = false,
  confirmationInput,
  testId,
  actionsTestId,
  confirmTestId,
  cancelTestId,
}: DeleteConfirmationProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        data-testid={testId}
        className="!w-[calc(100%-2rem)] !max-w-[528px] !rounded-2xl p-5 sm:p-6"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {confirmationInput && (
          <div className="space-y-2 text-left">
            <Label htmlFor={confirmationInput.id ?? 'delete-confirmation-input'}>
              {confirmationInput.label}
            </Label>
            <Input
              id={confirmationInput.id ?? 'delete-confirmation-input'}
              data-testid={confirmationInput.testId}
              value={confirmationInput.value}
              onChange={(event) => confirmationInput.onChange(event.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>
        )}

        <AlertDialogFooter
          data-testid={actionsTestId}
          className="flex-row gap-3 sm:space-x-0 [&>button]:min-w-0 [&>button]:flex-1"
        >
          <AlertDialogAction
            variant="destructive"
            className="flex-1"
            data-testid={confirmTestId}
            disabled={confirmDisabled || isConfirming}
            onClick={(event) => {
              event.preventDefault();
              void onConfirm();
            }}
          >
            {isConfirming ? confirmingLabel : confirmLabel}
          </AlertDialogAction>
          <AlertDialogCancel
            disabled={isConfirming}
            className="!mt-0 flex-1"
            data-testid={cancelTestId}
          >
            {cancelLabel}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
